import { Close, Store, Warning } from '@mui/icons-material'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import { create, useModal } from '@ebay/nice-modal-react'
import { Thumbnail } from '../../../../../../src/ui-components/products/Thumbnail'
import { observer } from 'mobx-react-lite'
import React, { useRef, useMemo } from 'react'
import { I18n, useBrandImage, useClickOutsideDetector } from 'common'
import { defaultTheme } from '../../../../../../src/theme/index'
import { breakpoints } from '../../../../../../src/theme/breakpoints'
import styled from 'styled-components'
import { zIndex } from '../../../../../../src/theme/zIndex'
import miscellaneousVehicle, {
  isMiscellaneousVehicle,
} from '../../../../../../src/features/search/Results/utils/miscellaneousVehicle'
import { ISeller } from '../../../../../../src/services/MultiSellerServicesProvider'
import { ProductLocationModel, ProductModel } from 'interfaces'
import { LocationType } from '../../../../../../src/helpers/locationType'
import { Hyperlink } from '../../../../../../src/ui-components/util/Hyperlink'
import VehicleCart from './VehicleCart'
import { Vehicle } from '../../../../../../src/store/models/Vehicles'
import { useSelector } from 'react-redux'
import { RootState } from 'store'

interface Props {
  product: ProductModel
  activeLocation: ProductLocationModel
  showLocationSelector?: () => void
  seller: ISeller
  currentVehicle: Vehicle
}

const SelectCartDrawer = create<Props>(
  observer<Props>((props: Props): React.ReactElement => {
    const modal = useModal()
    const {
      product,
      activeLocation,
      showLocationSelector,
      seller,
      currentVehicle,
    } = props
    // const onClose = vehicleHistoryStore.handleCloseNonAppProductModal
    const ref = useRef<HTMLInputElement>(null)

    useClickOutsideDetector([ref], modal.visible, modal.remove)

    const thumbnail = product?.partImages?.length
      ? product?.partImages.find((img) => img.type === 'thumbnail')?.url
      : ''

    const [brandImgUrl] = useBrandImage(product)

    const cartVehicles = useSelector(
      (state: RootState) => state.cart.cartData.vehicles
    )

    const vehicles = useMemo(() => {
      const vehicleList = cartVehicles?.map((cV) => cV.vehicle) || []

      const foundSelectedVehicle = vehicleList.some(
        (v) => v.id === currentVehicle.id
      )
      if (currentVehicle.id && !foundSelectedVehicle) {
        vehicleList.unshift(currentVehicle)
      }

      const foundMiscellanousVehicle = vehicleList.some(isMiscellaneousVehicle)
      if (!foundMiscellanousVehicle) {
        vehicleList.unshift(miscellaneousVehicle)
      }

      return vehicleList
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal.visible]) // Only update when modal opens

    const handleShowMoreLocations = (): void => {
      showLocationSelector()
      modal.remove()
    }

    function defaultModal() {
      return (
        <Container ref={ref}>
          <FlexColumn style={{ paddingBottom: 20 }}>
            <FlexRow style={{ justifyContent: 'space-between' }}>
              <Title>
                <I18n name="selectCartToAddItem" />
              </Title>
              <StyledClose onClick={modal.remove} />
            </FlexRow>

            <ProductDeatilContainer>
              <>
                <Thumbnail
                  urlOrBase64={thumbnail}
                  width={100}
                  height={100}
                  placeholder
                />

                {brandImgUrl && (
                  <BrandWrapper>
                    <Thumbnail
                      urlOrBase64={brandImgUrl}
                      width={100}
                      height={100}
                      alt="brand"
                    />
                  </BrandWrapper>
                )}
              </>

              <ProductDetails>
                <Subtitle color="black">
                  <Description> {product?.description}</Description>
                </Subtitle>
                <Details>
                  <Subtitle color="black">
                    Part # <SubtitleBold>{product?.partNumber} </SubtitleBold>
                  </Subtitle>

                  <Separator>|</Separator>

                  <Subtitle color="black">
                    Brand: <SubtitleBold>{product?.lineCode}</SubtitleBold>
                  </Subtitle>
                </Details>
                <Subtitle color="black">
                  <b>
                    {activeLocation?.locType === LocationType.VIEW_ONLY && (
                      <StyledWarning theme={defaultTheme} />
                    )}
                    {activeLocation?.qtyAvailable}&nbsp;
                    <I18n name="at" />
                    &nbsp;
                    {activeLocation?.locType === 'primary' && (
                      <Store htmlColor={defaultTheme.colors.green} />
                    )}
                    {activeLocation?.called}
                  </b>
                </Subtitle>

                {showLocationSelector && (
                  <Subtitle color="black">
                    <Hyperlink action={handleShowMoreLocations}>
                      <I18n name="moreLocations" />
                    </Hyperlink>
                  </Subtitle>
                )}
              </ProductDetails>
            </ProductDeatilContainer>
          </FlexColumn>

          <Divider style={{ marginLeft: -24, marginRight: -24, height: 0.2 }} />
          <CartItems>
            {vehicles?.map((vehicle) => (
              <VehicleCart
                key={`select_cart_drawer_cart_id_${vehicle?.id}`}
                seller={seller}
                product={product}
                location={activeLocation}
                vehicle={vehicle}
              />
            ))}
          </CartItems>
        </Container>
      )
    }

    const width = window.innerWidth

    if (!product) return null

    return (
      <ResponsiveDrawer
        anchor={width >= 1024 ? 'right' : 'bottom'}
        open={modal.visible}
        onClose={modal.remove}
        PaperProps={{
          square: false,
        }}
      >
        {defaultModal()}
      </ResponsiveDrawer>
    )
  })
)

export default SelectCartDrawer

const ResponsiveDrawer = styled(Drawer)`
  z-index: ${zIndex.modal};
`

const StyledClose = styled(Close)`
  font-size: 30px;
  cursor: pointer;
`

const Container = styled.div`
  height: 100%;
  width: 100%;
  min-width: 533px;
  background-color: ${defaultTheme.colors.componentsBackground};
  padding: 29px 24px 74px 24px;
  overflow-y: auto;
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`
const Details = styled.div`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  @media screen and (min-width: ${breakpoints.laptop}px) {
    flex-direction: row;
  }
`
const SubtitleBold = styled.span`
  font-weight: 400;
  @media screen and (min-width: ${breakpoints.laptop}px) {
    font-weight: 700;
  }
`
const Separator = styled.span`
  display: none;
  margin-left: 5px;
  margin-right: 5px;
  @media screen and (min-width: ${breakpoints.laptop}px) {
    display: block;
  }
`

const Title = styled.span`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  padding-bottom: 20px;
`
interface SubtitleProps {
  color: string
}
const Subtitle = styled.span<SubtitleProps>`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  /* identical to box height, or 171% */

  /* Black */

  color: ${(p) => p.color};

  a {
    color: ${(p) => p.color};
    font-weight: bold;
  }
`

const ProductDeatilContainer = styled.div`
  display: flex;
  gap: 20px;
  max-width: 500px;
`

const BrandWrapper = styled.div`
  margin-top: 20px;
`

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
`

const CartItems = styled(FlexColumn)`
  padding: 34px 0px 20px 0px;
  gap: 32px;
`

const Description = styled.p`
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  text-decoration-line: underline;
  @media screen and (min-width: ${breakpoints.laptop}px) {
    text-decoration-line: none;
  }
  margin-bottom: 0px;
`

const StyledWarning = styled(Warning)`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 18px;
  margin-top: auto;
  margin-bottom: auto;
  margin-right: 5px;
`
